<script lang="ts" setup>
import DynamicFormService from '../../_runtime/services/DynamicFormService';
import type { FormModel as ModuleModel } from '#layers/studio/app/runtime/services/ModulesService';
import ModulesService from '#layers/studio/app/runtime/services/ModulesService';

defineOptions({
    name: 'ModuleDynamicFormsSettings'
});

const { $alert } = useAlerts();
const { $log } = useLogging();

const model = defineModel<any>();
const defaults: any = {
    mode: 'module',
    dynamicFormOriginId: null,
    moduleId: null,
    sectionId: null
};
const modules = ref<Option<number>[]>([]);
const module = ref<ModuleModel>();
const modulesTriggerKey = ref(0);
const sections = computed(() => ([
    { value: null, text: '' },
    ...(module.value?.sections.map(p => ({ value: p.id, text: p.name })) ?? [])
]));

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };

    await loadModules();
    await loadModule(form.value.settings.moduleId);
});

watch(() => form.value?.settings, () =>
{
    model.value = form.value;
},
{ deep: true });

watch(() => form.value?.settings.moduleId, async (value, old) =>
{
    if (old)
    {
        form.value.settings.sectionId = null;
    }

    await loadModule(value);
});

async function fetchForm(id: any): Promise<any>
{
    const response = await DynamicFormService.fetchAutocompleteModel(id);

    return response || null;
}

async function searchForm(query: string, limit: number): Promise<any[]>
{
    return await DynamicFormService.getAutocompleteList(query, limit);
}

async function loadModules()
{
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const key = modulesTriggerKey.value;

    try
    {
        const pager = new Pager(1, 10000, 'Name', 'ASC');
        const response = await ModulesService.getList({} as any, pager);

        modules.value = [{ value: null, text: '' }, ...response.items.map(p => ({
            value: p.id,
            text: p.name
        }))];
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

async function loadModule(moduleId: number)
{
    if (!moduleId) return;

    try
    {
        const response = await ModulesService.fetch(moduleId);

        module.value = response.result;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}

async function onWizardDone(moduleId: number, sectionId: string)
{
    form.value.settings.moduleId = moduleId;
    form.value.settings.sectionId = sectionId;
    modulesTriggerKey.value++;
}
</script>

<template>
    <div class="alert alert-light pb-1">
        <IdeoFormGroup :label="$t('[[[Działanie modułu w oparciu o]]]')">
            <IdeoFormRadioGroup stacked>
                <IdeoFormRadio v-model="form.settings.mode" name="mode" value="module">
                    {{ $t('[[[Moduł dynamiczny]]]') }}
                </IdeoFormRadio>
            </IdeoFormRadioGroup>
        </IdeoFormGroup>
        <template v-if="form.settings.mode == 'form'">
            <IdeoFormGroup :label="$t('[[[Wybierz formularz]]]')" required>
                <div class="d-flex flex-column flex-md-row">
                    <div class="flex-grow-1">
                        <IdeoAutocomplete
                            v-model="form.settings.dynamicFormOriginId"
                            name="dynamicFormOriginId"
                            track-by="originId"
                            :placeholder="$t('[[[Wyszukaj formularz...]]]')"
                            :fetch="(id: number) => fetchForm(id)"
                            :search="(query: string, limit: number) => searchForm(query, limit)"
                        >
                            <template #selected="{ option }">
                                <div class="text-wrap">
                                    {{ option.title }}
                                </div>
                            </template>
                            <template #option="{ option }">
                                <div class="text-wrap">
                                    {{ option.title }}
                                </div>
                            </template>
                        </IdeoAutocomplete>
                    </div>
                </div>
            </IdeoFormGroup>
        </template>
        <template v-if="form.settings.mode == 'module'">
            <IdeoFormGroup :label="$t('[[[Moduł dynamiczny]]]')" :invalid-feedback="form.$errors.first('moduleId')" :state="form.$errors.state('moduleId')" required>
                <IdeoFormSelect v-model="form.settings.moduleId" type="text" name="moduleId" :options="modules" />
            </IdeoFormGroup>
            <IdeoFormGroup :label="$t('[[[Sekcja modułu]]]')" :invalid-feedback="form.$errors.first('sectionId')" :state="form.$errors.state('sectionId')" required>
                <IdeoFormSelect v-model="form.settings.sectionId" type="text" name="sectionId" :options="sections" :disabled="!form.settings.moduleId" />
            </IdeoFormGroup>
            <div class="align-self-end pb-3">
                <ModulesWizard
                    mode="form"
                    :expert="false"
                    :module="form.settings.moduleId"
                    size="small"
                    btn-class="ms-3"
                    @done="onWizardDone"
                />
            </div>
        </template>
    </div>
</template>
